/**
 * =============================================================================
 * USE ANNOUNCE HOOK - P1-A11Y-1
 * =============================================================================
 *
 * Provides screen reader announcements using aria-live regions.
 * Used to announce dynamic content changes to assistive technology users.
 *
 * WCAG 2.1 Reference:
 * - 4.1.3 Status Messages (Level AA): Status messages must be programmatically
 *   determinable through role or properties so they can be presented to the user
 *   by assistive technologies without receiving focus.
 *
 * Usage:
 * ```tsx
 * const { announce, Announcer } = useAnnounce();
 *
 * // Announce a message (polite priority)
 * announce('Question submitted successfully');
 *
 * // Announce with assertive priority (interrupts current speech)
 * announce('Form validation error', 'assertive');
 *
 * // Include Announcer component in your component tree
 * return (
 *   <>
 *     <Announcer />
 *     <YourContent />
 *   </>
 * );
 * ```
 *
 * =============================================================================
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import type { ReactElement } from 'react';

/**
 * Priority levels for screen reader announcements.
 * - polite: Waits for the screen reader to finish current content (default)
 * - assertive: Interrupts current content immediately (use sparingly)
 */
export type AnnouncePriority = 'polite' | 'assertive';

/**
 * Message structure for announcements.
 */
interface AnnounceMessage {
  id: number;
  text: string;
}

type AnnounceMessages = Record<AnnouncePriority, AnnounceMessage | null>;

/**
 * Result type for useAnnounce hook.
 */
export interface UseAnnounceResult {
  /** Function to announce a message to screen readers */
  announce: (message: string, priority?: AnnouncePriority) => void;
  /** Clear one live region and cancel only that priority's current lifetime */
  clear: (priority: AnnouncePriority) => void;
  /** Announcer component that must be rendered in the component tree */
  Announcer: () => ReactElement;
}

/**
 * Counter for unique message IDs.
 */
let messageIdCounter = 0;

/**
 * Hook for making screen reader announcements.
 *
 * Uses aria-live regions to announce content changes to assistive technology.
 * The Announcer component renders visually hidden text that screen readers
 * will read when the content changes.
 *
 * @returns Object containing announce function and Announcer component
 *
 * @example
 * ```tsx
 * function QuestionForm() {
 *   const { announce, Announcer } = useAnnounce();
 *
 *   const handleSubmit = async () => {
 *     try {
 *       await submitQuestion(data);
 *       announce('Question submitted successfully');
 *     } catch (error) {
 *       announce('Failed to submit question', 'assertive');
 *     }
 *   };
 *
 *   return (
 *     <>
 *       <Announcer />
 *       <form onSubmit={handleSubmit}>...</form>
 *     </>
 *   );
 * }
 * ```
 */
export function useAnnounce(): UseAnnounceResult {
  const [messages, setMessages] = useState<AnnounceMessages>({
    polite: null,
    assertive: null,
  });
  const messagesRef = useRef(messages);
  const timeoutRefs = useRef<Partial<Record<AnnouncePriority, number>>>({});
  messagesRef.current = messages;

  /**
   * Announce a message to screen readers.
   *
   * @param message - The message to announce
   * @param priority - 'polite' (default) or 'assertive'
   */
  const announce = useCallback((message: string, priority: AnnouncePriority = 'polite'): void => {
    // Each live region owns one current message and one independent lifetime.
    // Replacing instead of accumulating prevents an old message from being
    // re-announced when a newer status arrives.
    const previousTimeout = timeoutRefs.current[priority];
    if (previousTimeout !== undefined) {
      window.clearTimeout(previousTimeout);
    }

    const newMessage: AnnounceMessage = {
      id: ++messageIdCounter,
      text: message,
    };

    setMessages((previous) => ({ ...previous, [priority]: newMessage }));

    // Clear only the generation this timer owns. A later announcement of the
    // same priority must never be erased by an older timer.
    const timeout = window.setTimeout(() => {
      setMessages((previous) =>
        previous[priority]?.id === newMessage.id ? { ...previous, [priority]: null } : previous
      );
      if (timeoutRefs.current[priority] === timeout) {
        delete timeoutRefs.current[priority];
      }
    }, 3000);
    timeoutRefs.current[priority] = timeout;
  }, []);

  const clear = useCallback((priority: AnnouncePriority): void => {
    const timeout = timeoutRefs.current[priority];
    if (timeout !== undefined) {
      window.clearTimeout(timeout);
      delete timeoutRefs.current[priority];
    }
    setMessages((previous) =>
      previous[priority] === null ? previous : { ...previous, [priority]: null }
    );
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    const timeoutRegistry = timeoutRefs.current;
    return () => {
      for (const timeout of Object.values(timeoutRegistry)) {
        if (timeout !== undefined) window.clearTimeout(timeout);
      }
    };
  }, []);

  /**
   * Announcer component that renders the aria-live regions.
   * Must be included in the component tree for announcements to work.
   */
  const Announcer = useCallback((): ReactElement => {
    const current = messagesRef.current;

    return (
      <>
        {/* These parent live-region nodes remain mounted while keyed children
            change, including when identical text is announced twice. */}
        <div
          role="status"
          aria-live="polite"
          aria-atomic="true"
          className="fmp-visually-hidden"
          data-testid="announcer-polite"
        >
          {current.polite ? <span key={current.polite.id}>{current.polite.text}</span> : null}
        </div>
        <div
          role="alert"
          aria-live="assertive"
          aria-atomic="true"
          className="fmp-visually-hidden"
          data-testid="announcer-assertive"
        >
          {current.assertive ? (
            <span key={current.assertive.id}>{current.assertive.text}</span>
          ) : null}
        </div>
      </>
    );
  }, []);

  return { announce, clear, Announcer };
}
